import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, BookOpen, HelpCircle, Grid3x3, DoorClosed, CheckCircle2, Circle, Award, Download, ChevronRight, Blocks } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api, formatApiError, mediaUrl } from "../../lib/api";
import GamePlay from "../../components/os/games/GamePlay";

const ease = [0.16, 1, 0.3, 1];
const BLOCK_ICON = { lesson: BookOpen, trivia: HelpCircle, memorama: Grid3x3, escape: DoorClosed };

function renderContent(md) {
  return (md || "").split("\n").map((line, i) => {
    const t = line.trim();
    if (!t) return <div key={i} className="h-2" />;
    if (t.startsWith("### ")) return <h4 key={i} className="font-medium text-navy mt-4 mb-1">{t.slice(4)}</h4>;
    if (t.startsWith("## ")) return <h3 key={i} className="font-medium text-lg text-navy mt-5 mb-1">{t.slice(3)}</h3>;
    if (t.startsWith("# ")) return <h2 key={i} className="font-medium text-xl text-navy mt-5 mb-2">{t.slice(2)}</h2>;
    if (t.startsWith("- ") || t.startsWith("* ")) return <li key={i} className="ml-5 list-disc text-navy/70">{t.slice(2)}</li>;
    return <p key={i} className="text-navy/70 leading-relaxed">{t}</p>;
  });
}

export default function AuthoredCourse() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [err, setErr] = useState(null);
  const [sel, setSel] = useState(0);
  const [done, setDone] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [certId, setCertId] = useState(null);
  const [finalScore, setFinalScore] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/authoring/courses/${id}`).then((r) => {
      setCourse(r.data); setErr(null);
      const p = r.data.progress || {};
      setDone(p.done_blocks || []); setCompleted(!!p.completed); setCertId(p.certificate_id || null); setFinalScore(p.score ?? null);
    }).catch((e) => setErr(formatApiError(e.response?.data?.detail) || "No se pudo cargar el curso"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const blocks = course?.blocks || [];
  const block = blocks[sel];
  const isDone = (bid) => done.includes(bid);

  const applyProgress = (data) => {
    setDone(data.done_blocks || []);
    if (data.completed) { setCompleted(true); setCertId(data.certificate_id || null); setFinalScore(data.score ?? null); toast.success("¡Curso completado! 🎓"); }
  };

  const markLesson = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/authoring/courses/${id}/progress`, { block_id: block.id }); applyProgress(data); goNext(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(false); }
  };
  const submitGame = async (payload) => {
    const { data } = await api.post(`/authoring/courses/${id}/progress`, { block_id: block.id, ...payload });
    applyProgress(data);
    return data.block_result;
  };
  const checkStage = async (stage, answer) => {
    try { const { data } = await api.post(`/authoring/courses/${id}/check`, { block_id: block.id, stage, answer }); return !!data.correct; }
    catch { toast.error("No se pudo verificar"); return false; }
  };
  const goNext = () => { if (sel + 1 < blocks.length) setSel(sel + 1); };

  const download = async (cid) => {
    try {
      const r = await api.get(`/academy/certificates/${cid}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "certificado-omnia.pdf"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar el certificado"); }
  };

  if (err) return <OSLayout title="OMNIA Academy"><div className="py-24 text-center text-navy/50" data-testid="ac-error">{err}<div className="mt-3"><Link to="/os/academia" className="text-violet text-sm hover:underline">← Volver a Academy</Link></div></div></OSLayout>;
  if (!course) return <OSLayout title="OMNIA Academy"><div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-violet" /></div></OSLayout>;

  return (
    <OSLayout title={course.title}>
      <div className="space-y-6" data-testid="authored-course">
        <Link to="/os/academia" className="inline-flex items-center gap-2 text-sm text-navy/50 hover:text-navy"><ArrowLeft className="w-4 h-4" /> Volver a Academy</Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-violet"><Blocks className="w-5 h-5" /><span className="overline text-navy/40">Curso interactivo</span></div>
            <h1 className="text-3xl font-medium tracking-tight mt-1">{course.title}</h1>
            {course.description && <p className="text-navy/50 mt-1 text-sm max-w-2xl">{course.description}</p>}
          </div>
          {completed && certId && (
            <button data-testid="ac-cert-download" onClick={() => download(certId)} className="inline-flex items-center gap-2 rounded-full bg-gold/15 text-gold text-sm px-4 py-2.5 hover:bg-gold/25 shrink-0"><Award className="w-4 h-4" /> Certificado <Download className="w-4 h-4" /></button>
          )}
        </div>

        <div className="grid md:grid-cols-[260px_1fr] gap-6">
          <div className="space-y-1.5">
            {blocks.map((b, i) => {
              const Icon = BLOCK_ICON[b.type] || BookOpen;
              const active = i === sel;
              return (
                <button key={b.id} data-testid={`ac-block-${i}`} onClick={() => setSel(i)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-2.5 text-sm transition-colors ${active ? "bg-violet/10 text-violet" : "text-navy/60 hover:bg-navy/5"}`}>
                  {isDone(b.id) ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <Circle className="w-4 h-4 text-navy/20 shrink-0" />}
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="truncate flex-1">{b.title}</span>
                </button>
              );
            })}
          </div>

          <motion.div key={sel} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease }}
            className="bg-white border border-navy/10 rounded-2xl p-6 min-h-[320px]">
            {!block ? <p className="text-navy/40">Este curso no tiene bloques.</p> : block.type === "lesson" ? (
              <div>
                <h2 className="text-2xl font-medium mb-4">{block.title}</h2>
                {block.image && <img src={mediaUrl(block.image)} alt="" className="rounded-xl mb-4 max-h-72 w-full object-cover" />}
                <div className="prose-omnia space-y-1">{renderContent(block.content)}</div>
                <button data-testid="ac-lesson-next" onClick={markLesson} disabled={busy} className="mt-6 rounded-full bg-gradient-violet text-white text-sm px-6 py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{sel + 1 < blocks.length ? "Marcar y continuar" : "Marcar como visto"} <ChevronRight className="w-4 h-4" /></>}
                </button>
              </div>
            ) : (
              <div>
                <h2 className="text-2xl font-medium mb-4">{block.title}</h2>
                <GamePlay type={block.type} config={block.config} onSubmit={submitGame} onCheck={checkStage}
                  onContinue={goNext} continueLabel={sel + 1 < blocks.length ? "Siguiente bloque" : "Terminar"} />
              </div>
            )}
          </motion.div>
        </div>

        {completed && (
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-6 text-center" data-testid="ac-completed">
            <Award className="w-10 h-10 text-gold mx-auto" />
            <h3 className="text-xl font-medium mt-2">¡Completaste el curso!</h3>
            <p className="text-navy/55 text-sm mt-1">Puntaje: {finalScore ?? "—"}%</p>
            {certId && <button onClick={() => download(certId)} className="mt-4 rounded-full bg-gradient-violet text-white text-sm px-6 py-3 hover:opacity-90 inline-flex items-center gap-2"><Download className="w-4 h-4" /> Descargar certificado</button>}
          </div>
        )}
      </div>
    </OSLayout>
  );
}
